import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { toast } from "sonner";
import { Plus, Phone, EnvelopeSimple, Trash, X } from "@phosphor-icons/react";

const COLUMNS = [
  { id: "new", label: "New", bg: "bg-blue-50", border: "border-blue-200" },
  { id: "contacted", label: "Contacted", bg: "bg-yellow-50", border: "border-yellow-200" },
  { id: "converted", label: "Converted", bg: "bg-green-50", border: "border-green-200" },
  { id: "lost", label: "Lost", bg: "bg-red-50", border: "border-red-200" },
];

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/enquiries");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const g = { new: [], contacted: [], converted: [], lost: [] };
    items.forEach((it) => {
      (g[it.status] || g.new).push(it);
    });
    return g;
  }, [items]);

  const onDrop = async (e, status) => {
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const it = items.find((x) => x.id === id);
    if (!it || it.status === status || it.status === "converted") return;
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/enquiries/${id}`, { status });
      toast.success(`Moved to ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  return (
    <div data-testid="enquiries-page">
      <PageHeader
        title="Lead Pipeline"
        subtitle="Drag cards across the funnel to update status"
        actions={
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
            data-testid="new-enquiry-btn"
          >
            <Plus size={16} weight="bold" /> New enquiry
          </button>
        }
      />
      <div className="px-6 sm:px-10 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLUMNS.map((col) => (
            <div
              key={col.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, col.id)}
              className={`border ${col.border} ${col.bg} rounded-sm p-3 min-h-[400px]`}
              data-testid={`kanban-col-${col.id}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <h3 className="text-xs uppercase tracking-[0.25em] font-black text-zinc-700">{col.label}</h3>
                <span className="text-xs font-bold text-zinc-600 bg-white border border-zinc-300 rounded-sm px-2">
                  {grouped[col.id].length}
                </span>
              </div>
              <div className="space-y-2 kanban-col overflow-auto">
                {grouped[col.id].map((it) => (
                  <Card key={it.id} item={it} onClick={() => setEditing(it)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showNew ? <EnquiryModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} /> : null}
      {editing ? (
        <EnquiryModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      ) : null}
    </div>
  );
}

function Card({ item, onClick }) {
  const locked = item.status === "converted";

  return (
    <div
      draggable={!locked}
      onDragStart={(e) => {
        if (locked) return;
        e.dataTransfer.setData("text/plain", item.id);
      }}
      onClick={onClick}
      className={`bg-white border border-zinc-200 rounded-sm p-3 hover:border-zinc-900 transition-colors ${locked ? "cursor-not-allowed opacity-95" : "cursor-grab"}`}
      data-testid={`lead-card-${item.id}`}
    >
      <div className="font-bold text-sm text-zinc-900">{item.name}</div>
      <div className="mt-1 text-xs text-zinc-500 flex items-center gap-1">
        <Phone size={12} weight="bold" /> {item.phone}
      </div>
      {item.email ? (
        <div className="text-xs text-zinc-500 flex items-center gap-1">
          <EnvelopeSimple size={12} weight="bold" /> {item.email}
        </div>
      ) : null}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
          {item.service || "General"}
        </span>
        <div className="flex items-center gap-2">
          {locked ? <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-green-700">Locked</span> : null}
          {item.follow_up_date ? (
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E60000]">
              Due {item.follow_up_date}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EnquiryModal({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const locked = item?.status === "converted";
  const [form, setForm] = useState({
    name: item?.name || "",
    phone: item?.phone || "",
    email: item?.email || "",
    service: item?.service || "",
    source: item?.source || "other",
    message: item?.message || "",
    status: item?.status || "new",
    follow_up_date: item?.follow_up_date || "",
  });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, email: form.email || null, follow_up_date: form.follow_up_date || null };
      if (isEdit) {
        await api.patch(`/enquiries/${item.id}`, payload);
        if (note && !locked) await api.post(`/enquiries/${item.id}/notes`, { text: note });
      } else {
        await api.post("/enquiries", payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (locked) return;
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/enquiries/${item.id}`);
    toast.success("Deleted");
    onSaved();
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit enquiry" : "New enquiry"}>
      {locked ? (
        <div className="mb-4 border border-green-200 bg-green-50 rounded-sm px-3 py-2 text-sm text-green-800">
          This enquiry is converted, so further editing is locked.
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Inp label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="enq-name" disabled={locked} />
        <Inp label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="enq-phone" disabled={locked} />
        <Inp label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="enq-email" disabled={locked} />
        <Inp label="Service interested in" v={form.service} onChange={(v) => setForm({ ...form, service: v })} testid="enq-service" disabled={locked} />
        <Sel
          label="Source"
          v={form.source}
          onChange={(v) => setForm({ ...form, source: v })}
          opts={[["walk_in", "Walk-in"], ["phone", "Phone"], ["website", "Website"], ["social", "Social"], ["referral", "Referral"], ["other", "Other"]]}
          testid="enq-source"
          disabled={locked}
        />
        <Sel
          label="Status"
          v={form.status}
          onChange={(v) => setForm({ ...form, status: v })}
          opts={[["new", "New"], ["contacted", "Contacted"], ["converted", "Converted"], ["lost", "Lost"]]}
          testid="enq-status"
          disabled={locked}
        />
        <Inp label="Follow-up date" type="date" v={form.follow_up_date} onChange={(v) => setForm({ ...form, follow_up_date: v })} testid="enq-followup" disabled={locked} />
      </div>

      <div className="mt-3">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Message / Reason</label>
        <textarea
          rows={2}
          value={form.message}
          disabled={locked}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-500"
          data-testid="enq-message"
        />
      </div>

      {isEdit ? (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 mb-2">Activity log</div>
          <div className="space-y-2 max-h-40 overflow-auto border border-zinc-200 rounded-sm p-3 bg-zinc-50">
            {(item.notes_log || []).length === 0 ? <div className="text-xs text-zinc-500">No notes yet.</div> : null}
            {(item.notes_log || []).map((n) => (
              <div key={n.id} className="text-sm">
                <div className="text-zinc-900">{n.text}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{n.by} · {new Date(n.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <textarea
            rows={2}
            placeholder="Add a note (logged with your name & time)"
            value={note}
            disabled={locked}
            onChange={(e) => setNote(e.target.value)}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-500"
            data-testid="enq-add-note"
          />
        </div>
      ) : null}

      <div className="mt-5 flex items-center justify-between">
        {isEdit ? (
          <button
            onClick={remove}
            disabled={locked}
            className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="enq-delete"
          >
            <Trash size={14} weight="bold" /> Delete
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || locked}
            className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60"
            data-testid="enq-save"
          >
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-200 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h2>
          <button onClick={onClose} className="text-zinc-500"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Inp({ label, v, onChange, type = "text", testid, disabled = false }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">{label}</label>
      <input
        type={type}
        value={v}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-500"
        data-testid={testid}
      />
    </div>
  );
}

export function Sel({ label, v, onChange, opts, testid, disabled = false }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">{label}</label>
      <select
        value={v}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900 bg-white disabled:bg-zinc-50 disabled:text-zinc-500"
        data-testid={testid}
      >
        {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}
